import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

const spinnerVariants = cva('relative inline-block animate-spin', {
	variants: {
		size: {
			extrasmall: 'h-2 w-2', // 8px - Figma extrasmall
			small: 'h-4 w-4', // 16px - Figma small
			medium: 'h-6 w-6', // 24px - Figma medium
			large: 'h-8 w-8', // 32px - Figma large
			extralarge: 'h-16 w-16', // 64px - Figma extralarge
		},
		variant: {
			blue: '', // Figma blue: #8FD4F5 background, #075980 active border
			white: '', // Figma white: #D9D9D9 background, #FFFFFF active border
			grey: '', // Figma grey: #F0F0F0 background, #595959 active border
		},
	},
	defaultVariants: {
		size: 'medium',
		variant: 'blue',
	},
})

export interface SpinnerProps
	extends React.ComponentProps<'div'>,
		VariantProps<typeof spinnerVariants> {
	/**
	 * Spinner size variant - matches Figma design system
	 */
	size?: 'extrasmall' | 'small' | 'medium' | 'large' | 'extralarge'
	/**
	 * Spinner color variant - matches Figma design system colors
	 */
	variant?: 'blue' | 'white' | 'grey'
	/**
	 * Accessible label for screen readers
	 */
	'aria-label'?: string
}

const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
	(
		{
			className,
			size = 'medium',
			variant = 'blue',
			'aria-label': ariaLabel = 'Cargando...',
			...props
		},
		ref,
	) => {
		// Size-specific configurations per Figma specs
		const sizeConfigs = {
			extrasmall: {
				strokeWidth: 2,
				viewBox: 10,
				center: 5,
				radius: 3,
			},
			small: {
				strokeWidth: 3,
				viewBox: 20,
				center: 10,
				radius: 6.5,
			},
			medium: {
				strokeWidth: 5,
				viewBox: 30,
				center: 15,
				radius: 10,
			},
			large: {
				strokeWidth: '6px',
				viewBox: 38,
				center: 19,
				radius: 16,
			}, // Basado en el SVG de Figma
			extralarge: {
				strokeWidth: '8px',
				viewBox: 72,
				center: 36,
				radius: 28,
			},
		}

		// Color variants per Figma specs
		const colorStyles = {
			blue: {
				background: '#8FD4F5', // blue-200
				active: '#075980', // blue-800
			},
			white: {
				background: '#D9D9D9',
				active: '#FFFFFF',
			},
			grey: {
				background: '#F0F0F0',
				active: '#595959',
			},
		}

		const config = sizeConfigs[size]
		const colors = colorStyles[variant]

		// Generate the path for the active arc (scaled from Figma SVG)
		const generateArcPath = (center: number, radius: number) => {
			// Path del SVG de Figma para large (center=19, radius=16)
			// Escalamos proporcionalmente para otros tamaños
			const scale = radius / 16 // 16 es el radius del large en Figma

			// Path original de Figma: M19 3C21.4542 3 23.8755 3.56457...
			// Lo escalamos y centramos para cualquier tamaño
			const scaledPath = `M${center} ${center - radius}C${center + 2.4542 * scale} ${center - radius} ${center + 4.8755 * scale} ${center - radius + 0.56457 * scale} ${center + 7.0766 * scale} ${center - radius + 1.65004 * scale}C${center + 9.2777 * scale} ${center - radius + 2.7355 * scale} ${center + 11.1996 * scale} ${center - radius + 4.31277 * scale} ${center + 12.6936 * scale} ${center - radius + 6.25982 * scale}C${center + 14.1877 * scale} ${center - radius + 8.2069 * scale} ${center + 15.2138 * scale} ${center - radius + 10.4715 * scale} ${center + 15.6926 * scale} ${center - radius + 12.8786 * scale}C${center + 16.1714 * scale} ${center - radius + 15.2856 * scale} ${center + 16.09 * scale} ${center - radius + 17.7705 * scale} ${center + 15.4548 * scale} ${center - radius + 20.1411 * scale}C${center + 14.8196 * scale} ${center - radius + 22.5117 * scale} ${center + 13.6476 * scale} ${center - radius + 24.7044 * scale} ${center + 12.0294 * scale} ${center - radius + 26.5495 * scale}C${center + 10.4113 * scale} ${center - radius + 28.3947 * scale} ${center + 8.3903 * scale} ${center - radius + 29.8429 * scale} ${center + 6.1229 * scale} ${center - radius + 30.7821 * scale}C${center + 3.8555 * scale} ${center - radius + 31.7213 * scale} ${center + 1.4025 * scale} ${center - radius + 32.1263 * scale} ${center - 1.0465 * scale} ${center - radius + 31.9657 * scale}C${center - 3.4954 * scale} ${center - radius + 31.8052 * scale} ${center - 5.8746 * scale} ${center - radius + 31.0835 * scale} ${center - 8 * scale} ${center - radius + 29.8564 * scale}`

			return scaledPath
		}

		return (
			<div
				ref={ref}
				className={cn(spinnerVariants({ size, variant }), className)}
				role="status"
				aria-label={ariaLabel}
				{...props}
			>
				<svg
					width="100%"
					height="100%"
					viewBox={`0 0 ${config.viewBox} ${config.viewBox}`}
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					{/* Background ellipse */}
					<ellipse
						cx={config.center}
						cy={config.center}
						rx={config.radius}
						ry={config.radius}
						transform={`rotate(180 ${config.center} ${config.center})`}
						stroke={colors.background}
						strokeWidth={config.strokeWidth}
					/>
					{/* Active arc path */}
					<path
						d={generateArcPath(config.center, config.radius)}
						stroke={colors.active}
						strokeWidth={config.strokeWidth}
						strokeLinecap="round"
					/>
				</svg>
				<span className="sr-only">{ariaLabel}</span>
			</div>
		)
	},
)

Spinner.displayName = 'Spinner'

export { Spinner, spinnerVariants }
